import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Web storage utilities (replaces expo-secure-store)
export const storage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value)
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error('Failed to remove from localStorage:', error)
    }
  }
}

// Function to set up auth interceptors - called by authStore to break circular dependency
export const setupAuthInterceptors = (authStore: any) => {
  // Request interceptor to add auth token
  apiClient.interceptors.request.use(
    config => {
      const { accessToken } = authStore.getState()

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }

      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // Response interceptor to handle token refresh
  apiClient.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config

      // If there's no config (network error, etc.), just reject
      if (!originalRequest) {
        return Promise.reject(error)
      }

      // Don't retry logout, refresh, or already-retried requests
      const isAuthEndpoint =
        originalRequest.url?.includes('/auth/logout') ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login')

      // If error is 401 and we haven't retried yet and it's not an auth endpoint
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true

        try {
          await authStore.getState().refreshAccessToken()

          // Retry the original request with new token
          const { accessToken } = authStore.getState()
          originalRequest.headers.Authorization = `Bearer ${accessToken}`

          return apiClient(originalRequest)
        } catch (refreshError) {
          // Refresh failed, clear tokens locally without calling API
          storage.removeItem('accessToken')
          storage.removeItem('refreshToken')
          storage.removeItem('user')

          authStore.setState({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          })

          return Promise.reject(refreshError)
        }
      }

      return Promise.reject(error)
    }
  )
}
